import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { Form, Formik } from 'formik'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Popper,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'

import { i18n } from '../../translate/i18n'

import Autocomplete from '@material-ui/lab/Autocomplete'
import {
  Bot,
  ChevronDown,
  CircleX,
  Clock,
  Eye,
  EyeOff,
  Network,
  TriangleAlert
} from 'lucide-react'
import { AuthContext } from '../../context/Auth/AuthContext'
import toastError from '../../errors/toastError'
import { makeRandomId } from '../../helpers/makeRandomId'
import api from '../../services/api'
import {
  normalizeNotificationSoundPreferences,
  resolveNotificationSoundUrls
} from '../../utils/notificationSounds'
import AlertDialog from '../AlertDialog'
import AudioPlayer from '../AudioPlayer'
import { AvatarEditorModal } from '../AvatarEditorModal'
import BotConnectionsModal from '../BotConnectionsModal'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { InputField } from '../InputField'
import { InputSwitch } from '../InputSwitch'
import { QueueDepartmentSelect } from '../QueueDepartmentSelect'
import { SelectCountry } from '../SelectCountry'
import SelectInput from '../SelectInput'
import UserScheduleModal from '../UserScheduleModal'
import { useStyles } from './style'
import { UserPhoneCountry } from '../UserPhoneCountry'

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Required'),
  password: Yup.string()
    .min(5, 'Senha muito curta!')
    .max(50, 'Senha muito longa!'),
  email: Yup.string().email('Invalid email').required('Required'),
  profile: Yup.string().min(1, 'Selecione um perfil').required('Required')
})

const DEFAULT_COUNTRY_CODE = '+55'

const DEFAULT_WORK_SCHEDULE = {
  enabled: false,
  days: {
    0: {
      enabled: false,
      start: '08:00',
      end: '18:00',
      lunchStart: '',
      lunchEnd: ''
    },
    1: {
      enabled: true,
      start: '08:00',
      end: '18:00',
      lunchStart: '',
      lunchEnd: ''
    },
    2: {
      enabled: true,
      start: '08:00',
      end: '18:00',
      lunchStart: '',
      lunchEnd: ''
    },
    3: {
      enabled: true,
      start: '08:00',
      end: '18:00',
      lunchStart: '',
      lunchEnd: ''
    },
    4: {
      enabled: true,
      start: '08:00',
      end: '18:00',
      lunchStart: '',
      lunchEnd: ''
    },
    5: {
      enabled: true,
      start: '08:00',
      end: '18:00',
      lunchStart: '',
      lunchEnd: ''
    },
    6: {
      enabled: false,
      start: '08:00',
      end: '18:00',
      lunchStart: '',
      lunchEnd: ''
    }
  }
}

const WEEK_DAYS = [
  { key: '1', label: 'Segunda' },
  { key: '2', label: 'Terca' },
  { key: '3', label: 'Quarta' },
  { key: '4', label: 'Quinta' },
  { key: '5', label: 'Sexta' },
  { key: '6', label: 'Sabado' },
  { key: '0', label: 'Domingo' }
]

const AVAILABLE_EXTRA_ACCESSES = [
  'conexões',
  'usuarios',
  'supervisor',
  'departamentos',
  'relatorios',
  'auditoria'
]

const INITIAL_STATE = {
  name: '',
  email: '',
  password: '',
  profile: '',
  telefone: '',
  foco: false,
  ativo: true,
  addToNewDepartments: false,
  receivePendingTicketsNotifications: true,
  workSchedule: DEFAULT_WORK_SCHEDULE,
  restrictWorkTime: false,
  allowOvertime: false,
  overtimeLimit: '15',
  userRoles: [],
  selectedDepartments: [],
  selectedBotConnections: [],
  selectedWhatsappConnections: [],
  notificationSoundPreferences: normalizeNotificationSoundPreferences({})
}

const normalizeWorkSchedule = schedule => {
  const input = schedule && typeof schedule === 'object' ? schedule : {}
  const inputDays =
    input.days && typeof input.days === 'object' ? input.days : {}

  const normalizedDays = {}
  Object.keys(DEFAULT_WORK_SCHEDULE.days).forEach(dayKey => {
    const day = inputDays[dayKey] || {}
    normalizedDays[dayKey] = {
      enabled:
        typeof day.enabled === 'boolean'
          ? day.enabled
          : DEFAULT_WORK_SCHEDULE.days[dayKey].enabled,
      start: day.start || DEFAULT_WORK_SCHEDULE.days[dayKey].start,
      end: day.end || DEFAULT_WORK_SCHEDULE.days[dayKey].end,
      lunchStart:
        day.lunchStart || DEFAULT_WORK_SCHEDULE.days[dayKey].lunchStart,
      lunchEnd: day.lunchEnd || DEFAULT_WORK_SCHEDULE.days[dayKey].lunchEnd
    }
  })

  return {
    enabled: input.enabled === true,
    allowOvertime: input.allowOvertime === true,
    overtimeLimit: input.overtimeLimit || '15',
    days: normalizedDays
  }
}

const CustomPopper = React.memo(props => (
  <Popper
    {...props}
    placement="bottom-start"
    modifiers={{ flip: { enabled: false } }}
  />
))
CustomPopper.displayName = 'CustomPopper'

const AccessibleDepartmentsSync = ({
  selectedConnections,
  allDepartments,
  selectedDepartments,
  setFieldValue,
  onAccessibleDepartmentsChange
}) => {
  useEffect(() => {
    const botQueueIds = Array.from(
      new Set(
        selectedConnections
          .flatMap(conn => conn.queues || [])
          .filter(q => q.isBot)
          .map(q => Number(q.id))
      )
    )

    const applyDepartments = departments => {
      const normalizedDepartments = (departments || []).map(d => ({
        ...d,
        id: Number(d.id)
      }))
      onAccessibleDepartmentsChange(normalizedDepartments)

      const allowedIds = new Set(normalizedDepartments.map(d => d.id))
      const filteredDepartments = selectedDepartments.filter(d =>
        allowedIds.has(Number(d.id))
      )

      if (filteredDepartments.length !== selectedDepartments.length) {
        setFieldValue('selectedDepartments', filteredDepartments)
      }
    }

    if (botQueueIds.length === 0) {
      applyDepartments(allDepartments)
      return
    }

    let isMounted = true
    const fetchAccessibleDepartments = async () => {
      try {
        const { data } = await api.get(
          `/queue/accessible-by-bots?queueIds=${botQueueIds.join(',')}`
        )
        if (isMounted) {
          applyDepartments(data || [])
        }
      } catch (err) {
        if (isMounted) {
          toastError(err)
          applyDepartments([])
        }
      }
    }

    fetchAccessibleDepartments()
    return () => {
      isMounted = false
    }
  }, [
    selectedConnections,
    allDepartments,
    selectedDepartments,
    setFieldValue,
    onAccessibleDepartmentsChange
  ])

  return null
}

const sanitizePhoneNumber = (phoneStr, countryCodeStr) => {
  if (!phoneStr) return ''

  let cleanPhone = phoneStr.replace(/\D/g, '')
  let cleanDDI = countryCodeStr ? countryCodeStr.replace(/\D/g, '') : '55'

  if (cleanPhone.startsWith(cleanDDI)) {
    cleanPhone = cleanPhone.substring(cleanDDI.length)
  }

  if (cleanDDI === '55' && cleanPhone.length === 11 && cleanPhone[2] === '9') {
    cleanPhone = cleanPhone.substring(0, 2) + cleanPhone.substring(3)
  }

  return cleanPhone
}

const injectNinthDigitForBrazil = (phoneStr, countryCodeStr) => {
  if (!phoneStr) return ''
  let cleanPhone = phoneStr.replace(/\D/g, '')
  const cleanDDI = countryCodeStr ? countryCodeStr.replace(/\D/g, '') : '55'

  if (cleanDDI === '55' && cleanPhone.length === 10) {
    cleanPhone = cleanPhone.substring(0, 2) + '9' + cleanPhone.substring(2)
  }

  return cleanPhone
}

const UserModal = ({ open, onClose, onSave, userId }) => {
  const classes = useStyles()
  const theme = useTheme()
  const { user: loggedInUser } = useContext(AuthContext)

  const [showPassword, setShowPassword] = useState(false)
  const [user, setUser] = useState(INITIAL_STATE)
  const [avatar, setAvatar] = useState('')
  const [avatarName, setAvatarName] = useState('')
  const [isProcessingImage, setIsProcessingImage] = useState(false)
  const [departmentsLoaded, setDepartmentsLoaded] = useState(false)
  const [allDepartments, setAllDepartments] = useState([])
  const [accessibleDepartments, setAccessibleDepartments] = useState([])
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE)
  const [phoneError, setPhoneError] = useState('')
  const [isCheckingPhone, setIsCheckingPhone] = useState(false)
  const [botConnectionsModalOpen, setBotConnectionsModalOpen] = useState(false)
  const [botConnections, setBotConnections] = useState([])
  const [whatsappConnectionsModalOpen, setWhatsappConnectionsModalOpen] =
    useState(false)
  const [whatsappConnections, setWhatsappConnections] = useState([])
  const [notificationSounds, setNotificationSounds] = useState([])
  const [userScheduleModalOpen, setUserScheduleModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)

  const [availableRoles, setAvailableRoles] = useState([])
  const [loggedInUserRoles, setLoggedInUserRoles] = useState([])
  const [isDeleting] = useState(false)

  const [warningConfig, setWarningConfig] = useState({
    title: 'Atenção!',
    message: '',
    open: false
  })

  const triggerWarning = useCallback((message, title = 'Atenção!') => {
    setWarningConfig({ open: true, title, message })
  }, [])

  // useEffect(() => {
  //   const fetchNotificationSounds = async () => {
  //     try {
  //       const { data } = await api.get('/notification-sounds')
  //       setNotificationSounds(data?.sounds || [])
  //     } catch (err) {
  //       toastError(err)
  //       setNotificationSounds([])
  //     }
  //   }
  //   const fetchBotConnections = async () => {
  //     try {
  //       const { data } = await api.get('/queue')
  //       const bots = (data || []).filter(queue => queue.isBot === true)
  //       setBotConnections(bots)
  //     } catch (err) {
  //       toastError(err)
  //       setBotConnections([])
  //     }
  //   }
  //   if (open) {
  //     fetchNotificationSounds()
  //     fetchBotConnections()
  //   }
  // }, [open])

  useEffect(() => {
    if (!open) return

    const fetchModalData = async () => {
      try {
        const [soundsRes, queuesRes, whatsappsRes] = await Promise.all([
          api.get('/notification-sounds'),
          api.get('/queue'),
          api.get('/whatsapp/?session=0')
        ])
        setNotificationSounds(soundsRes.data?.sounds || [])
        setAllDepartments((queuesRes.data || []).filter(q => q.isBot !== true))
        setBotConnections((queuesRes.data || []).filter(q => q.isBot === true))
        setWhatsappConnections(
          (whatsappsRes.data || []).map(conn => ({
            ...conn,
            id: Number(conn.id),
            queues: (conn.queues || []).map(q => ({ ...q, id: Number(q.id) }))
          }))
        )
      } catch (err) {
        toastError(err)
      }
    }

    fetchModalData()
  }, [open])

  const loadAvatar = async () => {
    if (!userId) {
      setAvatar('')
      return
    }
    try {
      const avatarUrl = await api.get(`/users/${userId}/avatar`)

      // Se a API devolver dados E não for o avatar padrão genérico
      if (avatarUrl?.data && !avatarUrl.data.includes('default-avatar.png')) {
        setAvatar(`${avatarUrl.data}?cacheBusting=${makeRandomId(5)}`)
      } else {
        // Se for null ou a imagem genérica, setamos vazio para gerar as iniciais
        setAvatar('')
      }
    } catch (err) {
      console.warn('Falha ao carregar a imagem')
      setAvatar('')
    }
  }

  const fetchAuxiliaryData = async () => {
    try {
      const [rolesRes, loggedInRolesRes] = await Promise.all([
        api.get('/role/list'),
        api.get(`/role/${loggedInUser.id}`)
      ])
      setAvailableRoles(rolesRes.data || [])
      setLoggedInUserRoles(loggedInRolesRes.data || [])
    } catch (err) {
      if (err?.response?.status !== 401) {
        console.error('Error fetching auxiliary roles:', err)
      }
    }
  }

  useEffect(() => {
    let isMounted = true

    const fetchUser = async () => {
      if (!isMounted) return
      fetchAuxiliaryData()

      if (!userId) {
        setUser(INITIAL_STATE)
        setCountryCode(DEFAULT_COUNTRY_CODE)
        setDepartmentsLoaded(true)
        setAvatarName('')
        return
      }

      try {
        const { data } = await api.get(`/users/${userId}`)
        if (!isMounted) return

        setAvatarName(data.name || '')

        const rolesData = data.roles || []
        const roleNames = rolesData.map(r => r.role)
        let derivedProfile = 'usuarios'

        if (roleNames.includes('super')) derivedProfile = 'super'
        else if (roleNames.includes('admin')) derivedProfile = 'admin'
        else if (roleNames.includes('gestor')) derivedProfile = 'gestor'

        const userRoles = [...rolesData]
        const userDepts = data.queues
          ? data.queues.filter(q => q.isBot !== true)
          : []
        const uBotConns = data.queues
          ? data.queues.filter(q => q.isBot === true)
          : []
        const uWhatsappConns = data.whatsapps || []

        const normalizedCountryCode = data.countryCode
          ? `+${String(data.countryCode).replace(/\D/g, '')}`
          : DEFAULT_COUNTRY_CODE
        let cCode = normalizedCountryCode
        let displayPhoneNumber = data.telefone || ''
        if (displayPhoneNumber) {
          displayPhoneNumber = displayPhoneNumber
            .replace(cCode, '')
            .replace(/\D/g, '')

          displayPhoneNumber = injectNinthDigitForBrazil(
            displayPhoneNumber,
            cCode
          )
        }

        setUser(prevState => ({
          ...prevState,
          ...data,
          workSchedule: normalizeWorkSchedule(data.workSchedule),
          allowOvertime: data.workSchedule?.allowOvertime === true,
          overtimeLimit: data.workSchedule?.overtimeLimit || '15',
          profile: derivedProfile,
          userRoles: userRoles,
          selectedDepartments: userDepts,
          selectedBotConnections: uBotConns,
          selectedWhatsappConnections: uWhatsappConns,
          addToNewDepartments:
            data.addToNewDepartments === true ||
            data.addToNewDepartments === 'true',
          receivePendingTicketsNotifications:
            data.receivePendingTicketsNotifications !== false &&
            data.receivePendingTicketsNotifications !== 'false',
          foco: data.foco === true || data.foco === 'true',
          ativo: data.ativo !== false && data.ativo !== 'false',
          telefone: displayPhoneNumber,
          notificationSoundPreferences: normalizeNotificationSoundPreferences(
            data.notificationSoundPreferences
          )
        }))

        setCountryCode(cCode)
        setDepartmentsLoaded(true)
      } catch (err) {
        if (isMounted) {
          toastError(err)
          setDepartmentsLoaded(true)
        }
      }
    }

    fetchUser()
    loadAvatar()

    return () => {
      isMounted = false
    }
  }, [userId, open, loggedInUser.id])

  const handleClose = useCallback(() => {
    if (isDeleting) return
    if (avatar.startsWith('blob:')) {
      URL.revokeObjectURL(avatar)
    }
    onClose()
    setUser(INITIAL_STATE)
    setCountryCode(DEFAULT_COUNTRY_CODE)
    setSelectedFile(null)
    setAvatarName('')
  }, [avatar, onClose])

  const syncUserRoles = async (
    targetUserId,
    selectedMainProfile,
    extraAccesses
  ) => {
    try {
      const { data: currentRolesData } = await api.get(`/role/${targetUserId}`)
      const currentRoleNames = currentRolesData.map(r => r.role)

      const targetRolesSet = new Set()
      if (selectedMainProfile === 'super') {
        targetRolesSet.add('super')
        targetRolesSet.add('admin')
      } else if (selectedMainProfile === 'admin') {
        targetRolesSet.add('admin')
      } else if (selectedMainProfile === 'gestor') {
        targetRolesSet.add('gestor')

        extraAccesses.forEach(role => {
          targetRolesSet.add(role)
        })
      }

      const targetRolesArray = Array.from(targetRolesSet)

      const rolesToAdd = targetRolesArray.filter(
        role => !currentRoleNames.includes(role)
      )

      const rolesToRemove = currentRoleNames.filter(
        role => !targetRolesSet.has(role)
      )

      for (const roleName of rolesToAdd) {
        try {
          await api.post('/role', { userId: targetUserId, role: roleName })
        } catch (err) {
          console.error(`Erro ao adicionar role ${roleName}:`, err)
        }
      }

      for (const roleName of rolesToRemove) {
        try {
          await api.delete(`/role/${targetUserId}/${roleName}`)
        } catch (err) {
          console.warn(
            `Erro ao remover role ${roleName} (Provável falta de permissão):`,
            err
          )
        }
      }
    } catch (err) {
      console.error('Erro crítico na reconciliação de roles:', err)
    }
  }

  const handleSaveUser = async values => {
    const cleanPhoneNumber = sanitizePhoneNumber(values.telefone, countryCode)

    if (cleanPhoneNumber.length > 0) {
      const isBrazil = countryCode.replace(/\D/g, '') === '55'

      const visualPhoneLength = values.telefone.replace(/\D/g, '').length
      if (isBrazil && visualPhoneLength !== 11) {
        triggerWarning(
          'O número de telefone deve conter 10 ou 11 dígitos, incluindo o DDD (Ex: 67912345678).',
          'Formato Inválido'
        )
        return
      }
      if (phoneError) {
        toast.error('Por favor, corrija os erros antes de salvar')
        return
      }
    }

    const isPrivilegedLoggedInUser =
      loggedInUser.super ||
      loggedInUser.profile === 'admin' ||
      loggedInUser.profile === 'gestor' ||
      loggedInUserRoles.some(r => ['super', 'admin', 'gestor'].includes(r.role))

    const isSelfProfileEdit =
      Boolean(userId) && Number(userId) === Number(loggedInUser.id)

    const mainProfileToSave =
      isSelfProfileEdit && !isPrivilegedLoggedInUser
        ? 'usuarios'
        : values.profile

    const queueIds = values.selectedDepartments.map(d => d.id)
    const botQueueIds = values.selectedBotConnections.map(b => b.id)
    const whatsappIds = values.selectedWhatsappConnections.map(w => w.id)

    const userData = {
      ...values,
      queueIds,
      botQueueIds,
      whatsappIds,
      telefone: cleanPhoneNumber || null,
      countryCode: countryCode ? countryCode.replace(/\D/g, '') : null,
      profile: ['super', 'admin'].includes(mainProfileToSave)
        ? 'admin'
        : 'user',
      workSchedule: {
        ...normalizeWorkSchedule(values.workSchedule),
        allowOvertime: values.allowOvertime || false,
        overtimeLimit: values.overtimeLimit || '15'
      }
    }

    if (isSelfProfileEdit) {
      delete userData.ativo
      delete userData.profile
    }

    try {
      let targetId = userId
      if (userId) {
        await api.put(`/users/${userId}`, userData)
      } else {
        const { data } = await api.post('/users', userData)
        targetId = data.id
      }

      if (selectedFile) {
        const formData = new FormData()
        formData.append('file', selectedFile)
        formData.append('userId', targetId)
        await api.post(`/users/avatar`, formData)
      }

      const extraAccesses =
        isSelfProfileEdit && !isPrivilegedLoggedInUser
          ? []
          : values.userRoles
            .filter(ur => AVAILABLE_EXTRA_ACCESSES.includes(ur.role))
            .map(ur => ur.role)

      if (!isSelfProfileEdit) {
        await syncUserRoles(targetId, mainProfileToSave, extraAccesses)
      }

      await api.put(
        `/notification-sounds/preferences/${targetId}`,
        normalizeNotificationSoundPreferences(
          values.notificationSoundPreferences
        )
      )
      toast.success(i18n.t('userModal.success'))

      if (onSave) onSave()
      handleClose()
    } catch (err) {
      const backendError =
        err.response?.data?.error || err.response?.data?.message || err.message
      if (backendError && backendError.includes('ERR_PHONE_DUPLICATED')) {
        triggerWarning(
          'Este telefone já está cadastrado em nosso sistema.',
          'Atenção'
        )
      } else {
        toastError(err)
      }
    }
  }

  const validatePhone = phone => {
    const phoneNumber = phone.replace(/\D/g, '')
    return phoneNumber.length === 10 || phoneNumber.length === 11
  }

  const checkPhoneExists = useCallback(
    async (phone, currentCountryCode, currentUserId) => {
      const cleanPhone = phone.replace(/\D/g, '')
      if (!cleanPhone) {
        setPhoneError('')
        return
      }
      const isBrazil = countryCode.replace(/\D/g, '') === '55'
      if (isBrazil && cleanPhone.length !== 11) {
        const formatMsg =
          'O número de telefone deve conter 10 ou 11 dígitos (DDD + Número).'
        setPhoneError(formatMsg)
        triggerWarning(formatMsg, 'Formato Inválido')
        return
      }
      const phoneNumberFormatted = sanitizePhoneNumber(phone, countryCode)
      const fullPhoneNumber =
        countryCode.replace(/\D/g, '') + phoneNumberFormatted
      setIsCheckingPhone(true)
      try {
        const { data } = await api.get('/users/check-phone', {
          params: {
            phone: phoneNumberFormatted,
            countryCode: countryCode ? countryCode.replace(/\D/g, '') : null,
            legacyPhone: fullPhoneNumber,
            excludeUserId: userId || null
          }
        })
        if (data.exists) {
          const msg =
            'Este telefone já está cadastrado no sistema para outro usuário.'
          setPhoneError(msg)
          triggerWarning(msg, 'Telefone Duplicado')
        } else {
          setPhoneError('')
        }
      } catch (err) {
        console.error('Erro ao verificar telefone:', err)
      } finally {
        setIsCheckingPhone(false)
      }
    },
    [triggerWarning]
  )

  const isSelfProfileEdit =
    Boolean(userId) && Number(userId) === Number(loggedInUser.id)

  const isSuperOrAdmin =
    loggedInUser.super ||
    ['super', 'admin'].includes(loggedInUser.profile) ||
    loggedInUserRoles.some(r => ['super', 'admin'].includes(r.role))

  const isGestor =
    loggedInUser.profile === 'gestor' ||
    loggedInUserRoles.some(r => r.role === 'gestor')

  const canSeeAdminSwitches = isSuperOrAdmin || isGestor
  const canEditPhoto = isSelfProfileEdit || isSuperOrAdmin

  const shouldHideAdminSections = isGestor && !isSuperOrAdmin

  const handleCroppedAvatarSave = useCallback(file => {
    const previewUrl = URL.createObjectURL(file)
    setAvatar(previewUrl)

    setSelectedFile(file)
  }, [])

  const iconColor =
    theme.palette.type === 'dark'
      ? theme.palette.dark_50.main
      : theme.palette.gray_400.main

  const departmentOptions = useMemo(() => {
    return accessibleDepartments
  }, [accessibleDepartments])

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        className={classes.dialog}
      >
        <Formik
          initialValues={{
            ...user,
            name: user.name || '',
            email: user.email || '',
            profile: user.profile || 'usuarios',
            userRoles: user.userRoles || [],
            selectedDepartments: user.selectedDepartments || [],
            selectedBotConnections: user.selectedBotConnections || [],
            selectedWhatsappConnections: user.selectedWhatsappConnections || [],
            telefone: user.telefone || '',
            number: user.number || ''
          }}
          enableReinitialize={true}
          validationSchema={UserSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveUser(values)
              actions.setSubmitting(false)
            }, 400)
          }}
        >
          {({
            touched,
            errors,
            isSubmitting,
            values,
            setFieldValue,
            handleBlur
          }) => {
            const normalizedSoundPreferences =
              normalizeNotificationSoundPreferences(
                values.notificationSoundPreferences
              )
            const soundUrls = resolveNotificationSoundUrls(
              notificationSounds,
              normalizedSoundPreferences
            )

            return (
              <Form>
                <AccessibleDepartmentsSync
                  selectedConnections={values.selectedWhatsappConnections}
                  allDepartments={allDepartments}
                  selectedDepartments={values.selectedDepartments}
                  setFieldValue={setFieldValue}
                  onAccessibleDepartmentsChange={setAccessibleDepartments}
                />
                <DialogContent className={classes.dialogContent}>
                  <Grid container spacing={1} className={classes.container}>
                    <Grid
                      item
                      xs={12}
                      component="article"
                      aria-labelledby="section-basic-info"
                    >
                      <div className={classes.sectionTitle}>
                        <Typography variant="subtitle1">
                          Informações do Usuário
                        </Typography>
                        <div className={classes.statusContainer}>
                          <span className={classes.statusInfo}>
                            {values.ativo ? 'Ativo' : 'Bloqueado'}
                          </span>
                          <span
                            key={values.ativo ? 'ativo' : 'bloqueado'}
                            className={`${classes.statusBadge} ${values.ativo ? classes.badgeAtivo : classes.badgeInativo}`}
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                    </Grid>

                    <Grid item xs={12} className={classes.gridContainerAvatar}>
                      <div className={classes.avatarContainer}>
                        <AvatarEditorModal
                          currentAvatar={avatar}
                          userName={avatarName}
                          onSaveFile={handleCroppedAvatarSave}
                          disabled={isProcessingImage}
                          isReadOnly={!canEditPhoto}
                        />
                      </div>
                      <Grid item xs={12} className={classes.multFieldLine}>
                        <Grid item xs={6}>
                          <InputField
                            label={i18n.t('userModal.form.name')}
                            name="name"
                            onBlur={e => {
                              handleBlur(e)
                              setAvatarName(e.target.value)
                            }}
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            fullWidth
                            InputProps={{ maxLength: 50 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <UserPhoneCountry
                            label="Telefone"
                            value={`+${countryCode.replace(/\D/g, '')}${values.telefone || ''}`}
                            onChange={data => {
                              setCountryCode(`+${data.countryCode}`)
                              setFieldValue('telefone', data.telefone)
                            }}
                            onBlur={() => checkPhoneExists(values.telefone)}
                            disabled={isCheckingPhone}
                          />

                          {(phoneError ||
                            (touched.telefone && errors.telefone)) && (
                              <Typography
                                variant="caption"
                                color="error"
                                style={{
                                  marginLeft: 14,
                                  marginTop: 4,
                                  display: 'block',
                                  fontFamily: 'Inter, sans-serif'
                                }}
                              >
                                {phoneError ||
                                  errors.telefone ||
                                  (isCheckingPhone ? 'Verificando...' : '')}
                              </Typography>
                            )}
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      component="article"
                      aria-labelledby="section-login"
                    >
                      <div className={classes.sectionTitle}>
                        <Typography variant="subtitle1">
                          Informações de Login
                        </Typography>
                      </div>
                      <Grid item xs={12} className={classes.multFieldLine}>
                        <Grid item xs={5}>
                          <InputField
                            label={i18n.t('userModal.form.email')}
                            name="email"
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <InputField
                            label={i18n.t('userModal.form.password')}
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            placeholder={
                              userId && !values.password ? '••••••' : ''
                            }
                            error={touched.password && Boolean(errors.password)}
                            helperText={touched.password && errors.password}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowPassword(e => !e)}
                                    className={classes.showPassword}
                                  >
                                    {showPassword ? (
                                      <EyeOff size={18} />
                                    ) : (
                                      <Eye size={18} />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={2}
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end'
                          }}
                        >
                          <InputSwitch
                            label="Ativo"
                            size="small"
                            name="ativo"
                            checked={values.ativo !== false}
                            disabled={isSelfProfileEdit || values.profile === 'super'}
                            onChange={event => {
                              if (isSelfProfileEdit || values.profile === 'super') {
                                return
                              }

                              const isActive = event.target.checked
                              setFieldValue('ativo', isActive)

                              if (!isActive) {
                                setFieldValue(
                                  'receivePendingTicketsNotifications',
                                  false
                                )
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {canSeeAdminSwitches && !shouldHideAdminSections && (
                      <>
                        <Grid
                          item
                          xs={12}
                          component="article"
                          aria-labelledby="section-admin"
                        >
                          <div className={classes.sectionTitle}>
                            <Typography variant="subtitle1">
                              Administrativo
                            </Typography>
                          </div>
                          <Grid container spacing={2}>
                            <Grid
                              item
                              xs={12}
                              className={classes.multFieldLine}
                            >
                              <Grid item xs={4}>
                                <SelectInput
                                  label="Perfil Principal"
                                  value={values.profile}
                                  disabled={isSelfProfileEdit}
                                  onChange={e => {
                                    if (isSelfProfileEdit) {
                                      return
                                    }
                                    const val = e.target.value
                                    setFieldValue('profile', val)
                                    setFieldValue('userRoles', [{ role: val }])
                                    if (val === 'super') {
                                      setFieldValue('ativo', true)
                                    }
                                  }}
                                >
                                  {!isSelfProfileEdit && (
                                    <MenuItem value="super">Super</MenuItem>
                                  )}
                                  {!isSelfProfileEdit && (
                                    <MenuItem value="admin">
                                      Administrador
                                    </MenuItem>
                                  )}
                                  {!isSelfProfileEdit && (
                                    <MenuItem value="gestor">Gestor</MenuItem>
                                  )}
                                  <MenuItem value="usuarios">Usuário</MenuItem>
                                </SelectInput>
                              </Grid>

                              {values.userRoles.some(
                                ur => ur.role === 'gestor'
                              ) && (
                                  <Grid item xs={8}>
                                    <FormControl
                                      className={classes.formControl}
                                      fullWidth
                                    >
                                      <FormLabel style={{ marginBottom: 8 }}>
                                        Acessos Extras
                                      </FormLabel>
                                      <Autocomplete
                                        multiple
                                        size="small"
                                        disableCloseOnSelect
                                        disabled={isSelfProfileEdit}
                                        options={AVAILABLE_EXTRA_ACCESSES}
                                        value={values.userRoles
                                          .filter(ur =>
                                            AVAILABLE_EXTRA_ACCESSES.includes(
                                              ur.role
                                            )
                                          )
                                          .map(ur => ur.role)}
                                        noOptionsText="Nenhum acesso disponível"
                                        PopperComponent={CustomPopper}
                                        popupIcon={
                                          <ChevronDown
                                            size={20}
                                            color={iconColor}
                                          />
                                        }
                                        classes={{
                                          listbox: classes.listbox,
                                          popper: classes.popper
                                        }}
                                        getOptionLabel={option =>
                                          option.charAt(0).toUpperCase() +
                                          option.slice(1)
                                        }
                                        onChange={(event, newValue) => {
                                          setFieldValue('userRoles', [
                                            { role: values.profile },
                                            ...newValue.map(role => ({ role }))
                                          ])
                                        }}
                                        renderOption={(option, { selected }) => (
                                          <>
                                            <Checkbox
                                              color="primary"
                                              style={{
                                                marginRight: 8,
                                                padding: 0
                                              }}
                                              checked={selected}
                                            />
                                            {option.charAt(0).toUpperCase() +
                                              option.slice(1)}
                                          </>
                                        )}
                                        renderTags={(val, getTagProps) => {
                                          const numToDisplay = 2
                                          const displayedOptions = val.slice(
                                            0,
                                            numToDisplay
                                          )
                                          const remainingCount =
                                            val.length - numToDisplay
                                          return (
                                            <>
                                              {displayedOptions.map(
                                                (option, index) => (
                                                  <Chip
                                                    key={option}
                                                    variant="outlined"
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                    size="small"
                                                    className={classes.chip}
                                                    deleteIcon={
                                                      <CircleX
                                                        size={14}
                                                        color={iconColor}
                                                      />
                                                    }
                                                    style={{
                                                      borderRadius: '4px',
                                                      height: '20px',
                                                      fontSize: '11px',
                                                      margin: '2px',
                                                      color: iconColor
                                                    }}
                                                  />
                                                )
                                              )}
                                              {remainingCount > 0 && (
                                                <Chip
                                                  label={`+${remainingCount}`}
                                                  size="small"
                                                  style={{
                                                    height: '20px',
                                                    fontSize: '11px',
                                                    borderRadius: '412px',
                                                    backgroundColor:
                                                      theme.palette.blue_300.main,
                                                    color:
                                                      theme.palette.white_100
                                                        .main,
                                                    fontWeight: 'bold'
                                                  }}
                                                />
                                              )}
                                            </>
                                          )
                                        }}
                                        renderInput={params => (
                                          <TextField
                                            {...params}
                                            variant="outlined"
                                            placeholder={
                                              values.userRoles.filter(ur =>
                                                AVAILABLE_EXTRA_ACCESSES.includes(
                                                  ur.role
                                                )
                                              ).length === 0
                                                ? 'Selecione'
                                                : ''
                                            }
                                          />
                                        )}
                                      />
                                    </FormControl>
                                  </Grid>
                                )}
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              className={classes.multFieldLine}
                            >
                              <Grid
                                item
                                xs={12}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  gap: '8px',
                                  alignItems: 'stretch'
                                }}
                              >
                                <Tooltip
                                  title="Conexões disponíveis para criar atendimentos"
                                  placement="top"
                                  arrow
                                >
                                  <span style={{ flex: 1 }}>
                                    <ButtonPrimary
                                      variant="outlined"
                                      color="primary"
                                      onClick={() =>
                                        setWhatsappConnectionsModalOpen(true)
                                      }
                                      label="Conexões / Departamentos"
                                      startIcon={<Network size={18} />}
                                      style={{
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        padding: '6px 10px'
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title="Visualizar fila do bot para os bots selecionados"
                                  placement="top"
                                  arrow
                                >
                                  <span style={{ flex: 1 }}>
                                    <ButtonPrimary
                                      variant="outlined"
                                      color="primary"
                                      onClick={() =>
                                        setBotConnectionsModalOpen(true)
                                      }
                                      label="Bots"
                                      startIcon={<Bot size={18} />}
                                      style={{
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        padding: '6px 10px'
                                      }}
                                    />
                                  </span>
                                </Tooltip>

                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </>
                    )}

                    <Grid
                      item
                      xs={12}
                      component="article"
                      aria-labelledby="section-sounds"
                    >
                      <div className={classes.sectionTitle}>
                        <Typography variant="subtitle1">
                          Sons de Notificação
                        </Typography>
                      </div>
                      <Grid container spacing={1}>
                        {[
                          {
                            label: 'Atendimento',
                            key: 'attendanceMessageSoundId',
                            url: soundUrls?.attendanceMessageSoundUrl
                          },
                          {
                            label: 'Chat Interno',
                            key: 'internalChatMessageSoundId',
                            url: soundUrls?.internalChatMessageSoundUrl
                          },
                          {
                            label: 'Grupos',
                            key: 'groupMessageSoundId',
                            url: soundUrls?.groupMessageSoundUrl
                          }
                        ].map(soundItem => {
                          const selectedId =
                            normalizedSoundPreferences[soundItem.key]
                          const hasSelectedSound = !!selectedId
                          return (
                            <Grid item xs={12} md={4} key={soundItem.key}>
                              <SelectInput
                                label={soundItem.label}
                                value={
                                  normalizedSoundPreferences[soundItem.key] ??
                                  ''
                                }
                                onChange={event =>
                                  setFieldValue(
                                    `notificationSoundPreferences.${soundItem.key}`,
                                    event.target.value === ''
                                      ? null
                                      : Number(event.target.value)
                                  )
                                }
                              >
                                <MenuItem value="">Padrão</MenuItem>
                                {notificationSounds.map(sound => (
                                  <MenuItem key={sound.id} value={sound.id}>
                                    {sound.name}
                                  </MenuItem>
                                ))}
                              </SelectInput>
                              {hasSelectedSound && soundItem.url && (
                                <Box mt={1} width="100%">
                                  <AudioPlayer
                                    audioUrl={soundItem.url}
                                    isMessageRight={false}
                                    variant="compact"
                                    showTime={false}
                                  />
                                </Box>
                              )}
                            </Grid>
                          )
                        })}
                      </Grid>
                    </Grid>

                    {canSeeAdminSwitches && !shouldHideAdminSections && (
                      <>
                        <Grid
                          item
                          xs={12}
                          component="article"
                          aria-labelledby="section-schedule"
                        >
                          <div className={classes.sectionTitle}>
                            <Typography variant="subtitle1">
                              Horário de Login
                            </Typography>
                          </div>
                          <Grid
                            item
                            xs={12}
                            className={classes.multFieldLine}
                            style={{ height: '34px' }}
                          >
                            <InputSwitch
                              label="Restringir login por horário de expediente"
                              size="small"
                              name="workScheduleEnabled"
                              checked={values.workSchedule?.enabled || false}
                              onChange={event =>
                                setFieldValue(
                                  'workSchedule.enabled',
                                  event.target.checked
                                )
                              }
                            />
                            {values.workSchedule?.enabled && (
                              <ButtonPrimary
                                size="small"
                                variant="outlined"
                                onClick={() => setUserScheduleModalOpen(true)}
                                label="Horários"
                                endIcon={<Clock size={16} />}
                              />
                            )}
                            <UserScheduleModal
                              open={userScheduleModalOpen}
                              onClose={() => setUserScheduleModalOpen(false)}
                              onSaved={newSchedule =>
                                setFieldValue('workSchedule', newSchedule)
                              }
                              workSchedule={values.workSchedule}
                              userId={userId}
                              weekDays={WEEK_DAYS}
                            />
                          </Grid>
                          <Grid item xs={12} className={classes.multFieldLine}>
                            <Grid
                              item
                              xs={4}
                              style={{ height: '34px', marginTop: '6px' }}
                            >
                              <InputSwitch
                                label="Permitir Hora Extra"
                                size="small"
                                name="allowOvertime"
                                checked={values.allowOvertime || false}
                                onChange={event =>
                                  setFieldValue(
                                    'allowOvertime',
                                    event.target.checked
                                  )
                                }
                              />
                            </Grid>
                            {values.allowOvertime && (
                              <Grid item xs={2}>
                                <SelectInput
                                  name="overtimeLimit"
                                  value={values.overtimeLimit || '15'}
                                  disabled={!values.allowOvertime}
                                  onChange={e =>
                                    setFieldValue(
                                      'overtimeLimit',
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                >
                                  <MenuItem value="15">15m</MenuItem>
                                  <MenuItem value="30">30m</MenuItem>
                                  <MenuItem value="1h">1h</MenuItem>
                                  <MenuItem value="2h">2h</MenuItem>
                                </SelectInput>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          component="article"
                          aria-labelledby="section-extra"
                        >
                          <div className={classes.sectionTitle}>
                            <Typography variant="subtitle1">Extra</Typography>
                          </div>
                          <Grid item xs={12} className={classes.multFieldLine}>
                            <Grid item xs={4}>
                              <InputSwitch
                                label="Adicionar a novos departamentos"
                                size="small"
                                name="addToNewDepartments"
                                checked={values.addToNewDepartments || false}
                                onChange={event =>
                                  setFieldValue(
                                    'addToNewDepartments',
                                    event.target.checked
                                  )
                                }
                              />
                            </Grid>
                            <Grid item xs={5}>
                              <InputSwitch
                                label="Receber notificações de atendimentos pendentes"
                                size="small"
                                name="receivePendingTicketsNotifications"
                                checked={
                                  values.receivePendingTicketsNotifications !==
                                  false
                                }
                                onChange={event =>
                                  setFieldValue(
                                    'receivePendingTicketsNotifications',
                                    event.target.checked
                                  )
                                }
                              />
                            </Grid>
                            {canSeeAdminSwitches && (
                              <Grid item xs={3}>
                                <InputSwitch
                                  label="Foco"
                                  size="small"
                                  name="foco"
                                  checked={values.foco || false}
                                  onChange={event =>
                                    setFieldValue('foco', event.target.checked)
                                  }
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <ButtonSecondary
                    onClick={handleClose}
                    color="secondary"
                    disabled={isSubmitting}
                    label={i18n.t('userModal.buttons.cancel')}
                  />
                  <ButtonPrimary
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.btnWrapper}
                    label={
                      userId
                        ? `${i18n.t('userModal.buttons.okEdit')}`
                        : `${i18n.t('userModal.buttons.okAdd')}`
                    }
                  />
                </DialogActions>

                <BotConnectionsModal
                  open={botConnectionsModalOpen}
                  onClose={() => setBotConnectionsModalOpen(false)}
                  connections={botConnections}
                  onSelect={bot => {
                    const currentBots = values.selectedBotConnections
                    if (currentBots.some(b => b.id === bot.id)) {
                      setFieldValue(
                        'selectedBotConnections',
                        currentBots.filter(b => b.id !== bot.id)
                      )
                    } else {
                      setFieldValue('selectedBotConnections', [
                        ...currentBots,
                        bot
                      ])
                    }
                  }}
                  selectedBots={values.selectedBotConnections}
                  title="Visualização da aba Bot"
                  emptyMessage="Nenhum bot encontrado."
                  connectionsAsSelect
                  onConnectionsChange={connections =>
                    setFieldValue('selectedBotConnections', connections)
                  }
                />
                <BotConnectionsModal
                  open={whatsappConnectionsModalOpen}
                  onClose={() => setWhatsappConnectionsModalOpen(false)}
                  connections={whatsappConnections}
                  onSelect={connection => {
                    const currentConnections = values.selectedWhatsappConnections
                    if (currentConnections.some(c => c.id === connection.id)) {
                      setFieldValue(
                        'selectedWhatsappConnections',
                        currentConnections.filter(c => c.id !== connection.id)
                      )
                    } else {
                      setFieldValue('selectedWhatsappConnections', [
                        ...currentConnections,
                        connection
                      ])
                    }
                  }}
                  selectedBots={values.selectedWhatsappConnections}
                  title="Conexões disponiveis para criar atendimento"
                  emptyMessage="Nenhuma conexão encontrada."
                  connectionsAsSelect
                  onConnectionsChange={connections =>
                    setFieldValue('selectedWhatsappConnections', connections)
                  }
                  showDepartmentSelect
                  departments={departmentOptions}
                  selectedDepartments={values.selectedDepartments}
                  onDepartmentChange={departments =>
                    setFieldValue('selectedDepartments', departments)
                  }
                />
              </Form>
            )
          }}
        </Formik>
      </Dialog>
      <AlertDialog
        open={warningConfig.open}
        onClose={() => setWarningConfig(prev => ({ ...prev, open: false }))}
        title={warningConfig.title}
        icon={<TriangleAlert size={34} />}
        buttonLabel="Entendi"
      >
        {warningConfig.message}
      </AlertDialog>
    </div>
  )
}

export default UserModal
